<template>
  <v-dialog v-model="model" max-width="760">
    <v-card class="k-card pa-3">
      <div class="text-h6 font-weight-bold mb-2">{{ dialogTitle }}</div>

      <v-text-field
        v-model="title"
        :label="$t('eventDialog.titleLabel')"
        outlined
        dense
        hide-details
        class="k-tight-field"
      />

      <!-- FECHA + RANGO RÁPIDO -->
      <v-row class="k-tight-row">
        <v-col cols="12" md="6" class="k-tight-col">
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="dateLabel"
                :label="$t('eventDialog.dateLabel')"
                outlined
                dense
                readonly
                hide-details
                class="k-tight-field"
                v-bind="attrs"
                v-on="on"
              />
            </template>

            <v-date-picker
              v-model="dateYMD"
              :locale="$i18n.locale"
              first-day-of-week="1"
              @input="onDatePicked"
            />
          </v-menu>
        </v-col>

        <v-col cols="12" md="6" class="k-tight-col">
          <div class="d-flex flex-wrap align-center k-chip-wrap">
            <v-chip
              medium
              color="primary"
              :outlined="quickPreset !== 'morning'"
              :dark="quickPreset === 'morning'"
              class="k-preset-chip"
              @click="applyPreset('morning')"
            >
              {{ $t("eventDialog.morning") }}
            </v-chip>

            <v-chip
              medium
              color="primary"
              :outlined="quickPreset !== 'afternoon'"
              :dark="quickPreset === 'afternoon'"
              class="k-preset-chip"
              @click="applyPreset('afternoon')"
            >
              {{ $t("eventDialog.afternoon") }}
            </v-chip>

            <v-chip
              medium
              color="primary"
              :outlined="quickPreset !== 'night'"
              :dark="quickPreset === 'night'"
              class="k-preset-chip"
              @click="applyPreset('night')"
            >
              {{ $t("eventDialog.night") }}
            </v-chip>

            <v-chip
              medium
              color="primary"
              :outlined="!allDay"
              :dark="allDay"
              class="k-preset-chip"
              @click="toggleAllDay"
            >
              {{ $t("eventDialog.allDay") }}
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <!-- HORAS (MÓVIL: WHEEL | WEB: GOOGLE CALENDAR STYLE) -->
      <v-row v-if="!allDay" class="k-tight-row">
        <!-- WEB / DESKTOP -->
        <template v-if="!isMobile">
          <v-col cols="12" md="6" class="k-tight-col">
            <v-autocomplete
              v-model="startTime"
              :items="timeOptions"
              :label="$t('eventDialog.startTime')"
              outlined
              dense
              hide-details
              class="k-tight-field"
              prepend-inner-icon="mdi-clock-outline"
              autocomplete="off"
              auto-select-first
              :menu-props="{ offsetY: true, maxHeight: 320 }"
              @blur="startTime = normalizeTo5(startTime)"
              @change="quickPreset = null"
            />
          </v-col>

          <v-col cols="12" md="6" class="k-tight-col">
            <v-autocomplete
              v-model="endTime"
              :items="timeOptions"
              :label="$t('eventDialog.endTime')"
              outlined
              dense
              hide-details
              class="k-tight-field"
              prepend-inner-icon="mdi-clock-outline"
              autocomplete="off"
              auto-select-first
              :menu-props="{ offsetY: true, maxHeight: 320 }"
              @blur="endTime = normalizeTo5(endTime)"
              @change="quickPreset = null"
            />
          </v-col>
        </template>

        <!-- MÓVIL -->
        <template v-else>
          <v-col cols="12" md="6" class="k-tight-col">
            <v-text-field
              :value="startTime"
              :label="$t('eventDialog.startTime')"
              outlined
              dense
              readonly
              hide-details
              class="k-tight-field"
              prepend-inner-icon="mdi-clock-outline"
              @click="openTimeDialog('start')"
            />
          </v-col>

          <v-col cols="12" md="6" class="k-tight-col">
            <v-text-field
              :value="endTime"
              :label="$t('eventDialog.endTime')"
              outlined
              dense
              readonly
              hide-details
              class="k-tight-field"
              prepend-inner-icon="mdi-clock-outline"
              @click="openTimeDialog('end')"
            />
          </v-col>
        </template>
      </v-row>

      <!-- DIALOG RELOJ (SOLO MÓVIL) -->
      <v-dialog v-model="timeDialog" max-width="360" v-if="isMobile">
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            {{ timeDialogTarget === "start" ? $t("eventDialog.startTimeDialog") : $t("eventDialog.endTimeDialog") }}
          </v-card-title>

          <v-card-text>
            <div class="k-wheel">
              <div class="k-wheel-marker"></div>

              <div
                class="k-wheel-col"
                ref="wheelH"
                :style="wheelPadStyle"
                @scroll.passive="onWheelScroll('h')"
                @touchend="onWheelEnd('h')"
                @mouseup="onWheelEnd('h')"
              >
                <div
                  v-for="h in hours"
                  :key="'h' + h"
                  class="k-wheel-item"
                  :class="{ 'is-active': h === wheelH }"
                  @click="setWheel('h', h)"
                >
                  {{ pad2(h) }}
                </div>
              </div>

              <div class="k-wheel-sep">:</div>

              <div
                class="k-wheel-col"
                ref="wheelM"
                :style="wheelPadStyle"
                @scroll.passive="onWheelScroll('m')"
                @touchend="onWheelEnd('m')"
                @mouseup="onWheelEnd('m')"
              >
                <div
                  v-for="m in minutes"
                  :key="'m' + m"
                  class="k-wheel-item"
                  :class="{ 'is-active': m === wheelM }"
                  @click="setWheel('m', m)"
                >
                  {{ pad2(m) }}
                </div>
              </div>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="cancelTimeDialog">{{ $t("common.cancel") }}</v-btn>
            <v-btn color="primary" depressed @click="confirmTimeDialog">{{ $t("common.ok") }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-divider class="my-3" />

      <!-- COMPARTIR (TABLA) -->
      <div class="d-flex align-center mb-2">
        <div class="font-weight-medium">{{ $t("eventDialog.shareWithGroups") }}</div>
        <v-spacer />
        <v-btn
          v-if="shareRows.length && !isEditing"
          text
          small
          class="k-clear-btn"
          @click="clearSharing"
        >
          {{ $t("common.clear") }}
        </v-btn>
      </div>

      <v-card outlined class="k-share-table">
        <div class="k-share-head">
          <div class="k-share-col k-col-name">{{ $t("eventDialog.group") }}</div>
          <div class="k-share-col k-col-check">{{ $t("eventDialog.view") }}</div>
          <div class="k-share-col k-col-check">{{ $t("eventDialog.participate") }}</div>
        </div>

        <v-divider />

        <div v-if="!shareRows.length" class="k-share-empty caption">
          {{ $t("eventDialog.noGroupsAvailable") }}
        </div>

        <div v-else class="k-share-body">
          <div v-for="row in shareRows" :key="row.groupId" class="k-share-row">
            <div class="k-share-col k-col-name">
              <div class="text-body-2 font-weight-medium text-truncate">
                {{ row.name }}
              </div>
            </div>

            <div class="k-share-col k-col-check">
              <v-checkbox
                class="k-mini-check"
                hide-details
                dense
                :disabled="isEditing"
                :input-value="row.mode === 'view'"
                @change="setShareMode(row.groupId, 'view')"
              />
            </div>

            <div class="k-share-col k-col-check">
              <v-checkbox
                class="k-mini-check"
                hide-details
                dense
                :disabled="isEditing"
                :input-value="row.mode === 'participate'"
                @change="setShareMode(row.groupId, 'participate')"
              />
            </div>
          </div>
        </div>
      </v-card>

      <div
        v-if="selectedShareCount"
        class="caption mt-1"
        :style="{ opacity: 0.85 }"
      >
        {{ $t("eventDialog.sharedWithGroups", { count: selectedShareCount }) }}
      </div>

      <v-divider class="my-3" />

      <div class="d-flex justify-end" style="gap: 10px">
        <v-btn text @click="model = false">{{ $t("common.cancel") }}</v-btn>
        <v-btn color="primary" depressed @click="submit" :disabled="!canSubmit">
          {{ submitLabel }}
        </v-btn>
      </div>

      <v-alert v-if="error" type="error" dense text class="mt-2">
        {{ error }}
      </v-alert>
    </v-card>
  </v-dialog>
</template>

<script>
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function parseYMD(ymd) {
  const [Y, M, D] = (ymd || "").split("-").map(Number);
  if (!Y || !M || !D) return null;
  return new Date(Y, M - 1, D, 0, 0, 0, 0);
}
function parseHHmm(hhmm) {
  const [h, m] = (hhmm || "").split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { h, m };
}
function buildDate(ymd, hhmm) {
  const base = parseYMD(ymd);
  const t = parseHHmm(hhmm);
  if (!base || !t) return null;
  return new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate(),
    t.h,
    t.m,
    0,
    0
  );
}

export default {
  props: {
    value: Boolean,
    groups: { type: Array, required: true },
    initialDate: { type: [Date, String], default: null },
    event: { type: Object, default: null },
    mode: { type: String, default: "create" },
    allowedGroupIds: { type: Array, default: () => [] },
  },

  data: () => ({
    title: "",

    // fecha/hora
    dateMenu: false,
    dateYMD: "",
    startTime: "09:00",
    endTime: "10:00",
    allDay: false,
    quickPreset: null,

    // wheel móvil
    timeDialog: false,
    timeDialogTarget: "start",
    wheelH: 9,
    wheelM: 0,
    wheelItemH: 44,
    wheelPad: 98, // se recalcula dinámicamente al abrir
    _wheelSnapTimer: null,

    // compartir
    shareRows: [],
    error: "",
  }),

  computed: {
    model: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },

    isMobile() {
      return this.$vuetify && this.$vuetify.breakpoint
        ? this.$vuetify.breakpoint.smAndDown
        : false;
    },

    wheelPadStyle() {
      const p = `${this.wheelPad}px`;
      return { paddingTop: p, paddingBottom: p };
    },

    dateLabel() {
      const d = parseYMD(this.dateYMD);
      if (!d) return "";
      return d.toLocaleDateString(this.$i18n.locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },

    timeOptions() {
      const out = [];
      for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 5) {
          out.push(`${pad2(h)}:${pad2(m)}`);
        }
      }
      return out;
    },

    hours() {
      return Array.from({ length: 24 }, (_, i) => i);
    },
    minutes() {
      return Array.from({ length: 12 }, (_, i) => i * 5);
    },

    selectedShareCount() {
      return (this.shareRows || []).filter((r) => !!r.mode).length;
    },

    sharePayload() {
      return (this.shareRows || [])
        .filter((r) => !!r.mode)
        .map((r) => ({ groupId: r.groupId, accessMode: r.mode }));
    },

    canSubmit() {
      if (!this.title.trim()) return false;
      if (!this.dateYMD) return false;

      if (this.allDay) return true;

      const s = buildDate(this.dateYMD, this.startTime);
      const e = buildDate(this.dateYMD, this.endTime);
      if (!s || !e) return false;
      return e > s;
    },

    isEditing() {
      return this.mode === "edit" && !!this.event?.id;
    },

    dialogTitle() {
      return this.isEditing
        ? this.$t("eventDialog.editTitle")
        : this.$t("eventDialog.title");
    },

    submitLabel() {
      return this.isEditing ? this.$t("common.save") : this.$t("common.create");
    },
  },

  watch: {
    model(v) {
      if (v) this.initDefaults();
      else this.reset();
    },

    event: {
      deep: false,
      handler() {
        if (this.model) this.initDefaults();
      },
    },

    groups: {
      deep: true,
      handler() {
        this.syncShareRows();
      },
    },
  },

  methods: {
    pad2,

    resolveInitialYMD() {
      if (typeof this.initialDate === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(this.initialDate)) return this.initialDate;
        const parsed = new Date(this.initialDate);
        if (!Number.isNaN(parsed.getTime())) return toYMD(parsed);
      } else if (this.initialDate instanceof Date) {
        if (!Number.isNaN(this.initialDate.getTime())) return toYMD(this.initialDate);
      }

      return toYMD(new Date());
    },

    toDate(value) {
      if (value?.toDate) return value.toDate();
      if (value?.seconds) return new Date(value.seconds * 1000);
      return value ? new Date(value) : null;
    },

    toHHmm(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
      return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    },

    initDefaults() {
      if (this.isEditing) {
        this.initFromEvent();
        return;
      }

      const now = new Date();
      this.dateYMD = this.resolveInitialYMD();

      const m = now.getMinutes();
      const rounded = m < 30 ? 30 : 0;
      const carry = m < 30 ? 0 : 1;
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours() + carry,
        rounded,
        0,
        0
      );
      const end = new Date(start.getTime() + 60 * 60 * 1000);

      this.startTime = `${pad2(start.getHours())}:${pad2(start.getMinutes())}`;
      this.endTime = `${pad2(end.getHours())}:${pad2(end.getMinutes())}`;

      this.allDay = false;
      this.quickPreset = null;
      this.error = "";

      this.syncShareRows();
    },

    initFromEvent() {
      const ev = this.event || {};
      const start = this.toDate(ev.startAt);
      const end = this.toDate(ev.endAt);

      this.title = ev.title || ev.name || "";
      this.dateYMD = start ? toYMD(start) : this.resolveInitialYMD();
      this.startTime = this.toHHmm(start) || "09:00";
      this.endTime = this.toHHmm(end) || "10:00";
      this.allDay = !!ev.allDay || this.detectAllDay(start, end);
      if (this.allDay) {
        this.startTime = "00:00";
        this.endTime = "23:59";
      }
      this.quickPreset = null;
      this.error = "";
      this.shareRows = [];
      this.syncShareRows();
    },

    detectAllDay(start, end) {
      if (!start || !end) return false;
      return (
        start.getHours() === 0 &&
        start.getMinutes() === 0 &&
        end.getHours() === 23 &&
        (end.getMinutes() === 59 || end.getMinutes() === 55)
      );
    },

    normalizeTo5(value) {
      if (!value) return value;
      const raw = String(value).trim();

      let h = null;
      let m = null;

      if (raw.includes(":")) {
        const parts = raw.split(":");
        h = Number(parts[0]);
        m = Number(parts[1]);
      } else {
        const digits = raw.replace(/\D/g, "");
        if (digits.length <= 2) {
          h = Number(digits);
          m = 0;
        } else {
          h = Number(digits.slice(0, digits.length - 2));
          m = Number(digits.slice(-2));
        }
      }

      if (!Number.isFinite(h) || !Number.isFinite(m)) return value;
      h = Math.min(23, Math.max(0, h));
      m = Math.min(59, Math.max(0, m));

      const m5 = Math.round(m / 5) * 5;
      const mm = (m5 + 60) % 60;

      return `${pad2(h)}:${pad2(mm)}`;
    },

    // -------- WHEEL (móvil) --------
    openTimeDialog(target) {
      this.timeDialogTarget = target;

      const src = target === "start" ? this.startTime : this.endTime;
      const [hh, mm] = (src || "09:00").split(":").map(Number);

      this.wheelH = Number.isFinite(hh) ? hh : 9;

      const mins = Number.isFinite(mm) ? mm : 0;
      const rounded = Math.round(mins / 5) * 5;
      this.wheelM = (rounded + 60) % 60;

      this.timeDialog = true;

      this.$nextTick(() => {
        // ✅ calcula padding real del contenedor (para que la franja coincida siempre)
        const el = this.$refs.wheelH;
        if (el && el.clientHeight) {
          this.wheelPad = Math.round((el.clientHeight - this.wheelItemH) / 2);
        }

        // ✅ alinear EXACTO (sin restar padding): top = idx * itemHeight
        this.scrollWheelTo("h", this.wheelH, false);
        this.scrollWheelTo("m", this.wheelM, false);
      });
    },

    cancelTimeDialog() {
      this.timeDialog = false;
    },

    confirmTimeDialog() {
      // fuerza lectura final por si hay momentum
      this.onWheelEnd("h");
      this.onWheelEnd("m");

      const picked = `${this.pad2(this.wheelH)}:${this.pad2(this.wheelM)}`;

      if (this.timeDialogTarget === "start") {
        this.startTime = picked;
        this.quickPreset = null;
      } else {
        this.endTime = picked;
        this.quickPreset = null;
      }

      const s = buildDate(this.dateYMD, this.startTime);
      const e = buildDate(this.dateYMD, this.endTime);
      if (s && e && e <= s) {
        const plus = new Date(s.getTime() + 60 * 60 * 1000);
        const hh = plus.getHours();
        const mm = plus.getMinutes();
        const mm5 = Math.round(mm / 5) * 5;
        this.endTime = `${this.pad2(hh)}:${this.pad2((mm5 + 60) % 60)}`;
      }

      this.timeDialog = false;
    },

    setWheel(which, val) {
      if (which === "h") this.wheelH = val;
      else this.wheelM = val;

      this.scrollWheelTo(which, val, true);
    },

    // ✅ clave: sin padding en el cálculo -> top = idx*itemH
    scrollWheelTo(which, val, smooth) {
      const el = which === "h" ? this.$refs.wheelH : this.$refs.wheelM;
      if (!el) return;

      const arr = which === "h" ? this.hours : this.minutes;
      const idx = arr.indexOf(val);
      if (idx === -1) return;

      const top = idx * this.wheelItemH;
      el.scrollTo({ top, behavior: smooth ? "smooth" : "auto" });
    },

    // ✅ valor real en la franja sombreada = round(scrollTop / itemH)
    readWheelValue(which) {
      const el = which === "h" ? this.$refs.wheelH : this.$refs.wheelM;
      if (!el) return null;

      const arr = which === "h" ? this.hours : this.minutes;
      const idx = Math.round(el.scrollTop / this.wheelItemH);
      const clamped = Math.max(0, Math.min(arr.length - 1, idx));
      return arr[clamped];
    },

    onWheelScroll(which) {
      const val = this.readWheelValue(which);
      if (val == null) return;

      if (which === "h") this.wheelH = val;
      else this.wheelM = val;

      clearTimeout(this._wheelSnapTimer);
      this._wheelSnapTimer = setTimeout(() => {
        const finalVal = this.readWheelValue(which);
        if (finalVal == null) return;

        if (which === "h") this.wheelH = finalVal;
        else this.wheelM = finalVal;

        // fuerza snap exacto al final (sin animación)
        this.scrollWheelTo(which, finalVal, false);
      }, 120);
    },

    onWheelEnd(which) {
      const finalVal = this.readWheelValue(which);
      if (finalVal == null) return;

      if (which === "h") this.wheelH = finalVal;
      else this.wheelM = finalVal;

      this.scrollWheelTo(which, finalVal, false);
    },

    // -------- resto --------
    reset() {
      this.title = "";
      this.dateMenu = false;

      this.dateYMD = "";
      this.startTime = "09:00";
      this.endTime = "10:00";

      this.allDay = false;
      this.quickPreset = null;

      this.timeDialog = false;
      this.timeDialogTarget = "start";
      this.wheelH = 9;
      this.wheelM = 0;

      this.shareRows = [];
      this.error = "";
    },

    syncShareRows() {
      const prev = new Map(
        (this.shareRows || []).map((r) => [r.groupId, r.mode])
      );
      const existingShare = this.isEditing ? this.shareModesFromEvent() : {};

      this.shareRows = (this.groups || []).map((g) => ({
        groupId: g.id,
        name: g.name,
        mode: prev.get(g.id) || existingShare[g.id] || null,
      }));
    },

    shareModesFromEvent() {
      const ev = this.event || {};
      const share = ev.shareByGroup && typeof ev.shareByGroup === "object"
        ? ev.shareByGroup
        : {};
      const accessByGroup = ev.accessByGroup && typeof ev.accessByGroup === "object"
        ? ev.accessByGroup
        : {};

      const out = {};
      const groupIds = Array.isArray(ev.groupIds) ? ev.groupIds : [];
      groupIds.forEach((gid) => {
        const id = String(gid);
        out[id] =
          share[id] === "view" || share[id] === "participate"
            ? share[id]
            : accessByGroup[id] === "view" || accessByGroup[id] === "participate"
              ? accessByGroup[id]
              : "participate";
      });
      return out;
    },

    setShareMode(groupId, mode) {
      if (this.isEditing) return;

      const idx = (this.shareRows || []).findIndex((r) => r.groupId === groupId);
      if (idx === -1) return;

      const current = this.shareRows[idx].mode;
      const next = current === mode ? null : mode;

      this.$set(this.shareRows, idx, { ...this.shareRows[idx], mode: next });
    },

    clearSharing() {
      if (this.isEditing) return;

      this.shareRows = (this.shareRows || []).map((r) => ({ ...r, mode: null }));
    },

    onDatePicked() {
      this.dateMenu = false;
      if (this.allDay) this.quickPreset = null;
    },

    toggleAllDay() {
      this.allDay = !this.allDay;
      this.quickPreset = null;

      if (this.allDay) {
        this.startTime = "00:00";
        this.endTime = "23:59";
      } else {
        this.startTime = "09:00";
        this.endTime = "10:00";
      }
    },

    applyPreset(which) {
      this.allDay = false;
      this.quickPreset = which;

      if (which === "morning") {
        this.startTime = "09:00";
        this.endTime = "12:00";
      } else if (which === "afternoon") {
        this.startTime = "15:00";
        this.endTime = "18:00";
      } else if (which === "night") {
        this.startTime = "20:00";
        this.endTime = "23:00";
      }
    },

    submit() {
      this.error = "";

      if (!this.dateYMD) {
        this.error = this.$t("eventDialog.invalidDate");
        return;
      }

      let startAt = null;
      let endAt = null;

      if (this.allDay) {
        startAt = buildDate(this.dateYMD, "00:00");
        endAt = buildDate(this.dateYMD, "23:59");
      } else {
        startAt = buildDate(this.dateYMD, this.startTime);
        endAt = buildDate(this.dateYMD, this.endTime);
      }

      if (!startAt || !endAt) {
        this.error = this.$t("eventDialog.invalidDateTime");
        return;
      }
      if (endAt <= startAt) {
        this.error = this.$t("eventDialog.endTimeAfterStart");
        return;
      }

      const share = this.sharePayload;
      const groupIds = share.map((x) => x.groupId);

      const accessMode = share.some((x) => x.accessMode === "participate")
        ? "participate"
        : "view";

      const payload = {
        title: this.title.trim(),
        startAt,
        endAt,
        groupIds,
        accessMode: groupIds.length ? accessMode : "view",
        shareByGroup: share,
        allDay: this.allDay,
      };

      if (this.isEditing) {
        this.$emit("update", {
          ...payload,
          id: this.event.id,
          eventId: this.event.id,
        });
      } else {
        this.$emit("create", payload);
      }

      this.model = false;
    },
  },
};
</script>

<style scoped>
/* --- DIALOG / CARD --- */
.k-card {
  padding: 18px !important;
}

/* --- ESPACIADOS GENERALES --- */
.k-tight-row {
  margin-top: 10px !important;
  margin-bottom: 6px !important;
}

.k-tight-col {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.k-tight-field {
  margin-top: 8px !important;
  margin-bottom: 8px !important;
}

/* --- CHIPS --- */
.k-chip-wrap {
  gap: 10px;
  padding-top: 6px;
  padding-bottom: 6px;
}
.k-preset-chip {
  font-weight: 700;
}

/* --- DIVIDERS --- */
.my-3 {
  margin-top: 14px !important;
  margin-bottom: 14px !important;
}

/* --- BOTONERA --- */
.d-flex.justify-end {
  margin-top: 6px;
}

/* --- TABLA COMPARTIR --- */
.k-share-table {
  border-radius: 14px;
  overflow: hidden;
}

.k-share-head,
.k-share-row {
  display: grid;
  grid-template-columns: 1fr 110px 110px;
  align-items: center;
}

.k-share-head {
  padding: 10px 12px;
  font-weight: 700;
  font-size: 12px;
  opacity: 0.85;
}

.k-share-body {
  padding: 2px 0;
}

.k-share-row {
  padding: 8px 12px;
}

.k-share-row + .k-share-row {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.k-share-col {
  min-width: 0;
}

.k-col-name {
  padding-right: 10px;
}

.k-col-check {
  display: flex;
  justify-content: center;
}

/* checkbox compacto */
.k-mini-check {
  margin: 0 !important;
  padding: 0 !important;
}

.k-share-empty {
  padding: 12px;
  opacity: 0.75;
}

.k-clear-btn {
  text-transform: none;
}

/* --- WHEEL (móvil) --- */
.k-wheel {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 24px 1fr;
  align-items: center;
  height: 240px;
}

.k-wheel-marker {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 44px;
  transform: translateY(-50%);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.06);
  pointer-events: none;
}

.k-wheel-col {
  height: 240px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-snap-type: y mandatory;
}
.k-wheel-col::-webkit-scrollbar {
  display: none;
}

.k-wheel-item {
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 700;
  scroll-snap-align: center;
  opacity: 0.35;
  user-select: none;
  cursor: pointer;
}

.k-wheel-item.is-active {
  opacity: 1;
}

.k-wheel-sep {
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  opacity: 0.7;
}

/* --- Autocomplete más "Google Calendar" --- */
.v-autocomplete .v-input__slot {
  border-radius: 12px !important;
}

/* --- AJUSTE MÓVIL --- */
@media (max-width: 600px) {
  .k-card {
    padding: 14px !important;
  }
  .k-tight-col {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }
  .k-tight-field {
    margin-top: 10px !important;
    margin-bottom: 10px !important;
  }
  .k-chip-wrap {
    gap: 8px;
  }

  .k-share-head,
  .k-share-row {
    grid-template-columns: 1fr 92px 92px;
  }
}
</style>
